import APIManager from 'api/APIManager';
import APIConstant from 'api/APIConstant';
import { parameterToPath } from 'utils';

export type Coupon = {
  coupon_id?: number; // 쿠폰 idx
  coupon_code?: string; // 발급 코드
  coupon_name?: string; // 쿠폰명
  coupon_issue_num?: number; // 발급 수량
  expiration_date: { [key: string]: string | number }; // 만료기간
  limit_day?: { [key: string]: string | number }; // 사용제한 요일
  limit_time?: { [key: string]: string | number }; // 사용제한 시간
  issue_type?: string; // 발급 유형
  available_platform?: { [key: string]: string | number }; // 사용가능 플랫폼
  payment_method?: { [key: string]: string | number }; // 사용가능 결제 수단
  is_duplication?: string; // 중복사용 여부
  minimum_price?: number; // 최소 사용 금액
  cost_share?: { [key: string]: string | number }; // 비용분담설정
  payment_type?: string; // 지급 유형
  is_reissue?: 'Y' | 'N';
  per_user_limit?: number;
  is_used?: 'Y' | 'N';
  created_at?: number; // 생성일
  updated_at?: number; // 수정일
};

export type MapCreateCouponRequest = {
  coupon: {
    coupon_name: string;
    coupon_code: string;
    coupon_content: string;
    coupon_issue_num: number;
    minimum_price: number;
    start_date: string | null;
    end_date: string | null;
    expiration_date: number;
    expiration_type: 'FIXED' | 'ROLLING';
    is_duplication: 'Y' | 'N';
    payment_type: 'MANUAL' | 'AUTOMATIC' | 'DOWNLOAD' | 'STAMP';
    coupon_type: 'FREQUENCY' | 'MENU' | 'MD' | 'PRODUCT' | 'DESERT' | 'ALL';
    issue_event:
      | 'SIGNUP'
      | 'FIRST_PURCHASE'
      | 'REVIEW_POSTED'
      | 'ORDER_COMPLETED'
      | 'DELIVERY_COMPLETED'
      | null;
    disabled_shop: { shop_information_id: number }[];
    is_reissue: 'Y' | 'N';
    per_user_limit: number;
    is_used: 'Y' | 'N';
  };
  product_policy: {
    policy_type: 'COUPON';
    discount_rate: number;
    reserve_rate: number;
    discount_type: 'W' | 'R';
    reserve_type: 'W' | 'R';
    commission_rate: number;
    share_ratio: number;
    is_show: 'Y' | 'N';
  };
  menu_ids: { menu_id: number }[];
};

export type MapCreateProductCouponRequest = {
  coupon: {
    coupon_name: string;
    coupon_code: string;
    coupon_content: string;
    coupon_issue_num: number;
    minimum_price: number;
    start_date: string | null;
    end_date: string | null;
    expiration_date: number;
    expiration_type: 'FIXED' | 'ROLLING';
    is_duplication: 'Y' | 'N';
    payment_type: 'MANUAL' | 'AUTOMATIC' | 'DOWNLOAD' | 'STAMP';
    coupon_type: 'FREQUENCY' | 'MENU' | 'MD' | 'PRODUCT' | 'DESERT' | 'ALL';
    issue_event:
      | 'SIGNUP'
      | 'FIRST_PURCHASE'
      | 'REVIEW_POSTED'
      | 'ORDER_COMPLETED'
      | 'DELIVERY_COMPLETED'
      | null;
    disabled_shop: { shop_information_id: number }[];
  };
  product_policy: {
    policy_type: 'COUPON';
    discount_rate: number;
    reserve_rate: number;
    discount_type: 'W' | 'R';
    reserve_type: 'W' | 'R';
    commission_rate: number;
    share_ratio: number;
    is_show: 'Y' | 'N';
  };
  product_ids: { product_id: number }[];
};

export type UpdateMapMenuPolicy = {
  map_menu_policy_id: number;
  menu_id: number;
  product_policy_id: number;
  policy_status: 'EXPIRED' | 'POSSIBLE';
  created_at: number;
  is_enable: 'N' | 'Y';
};

const $http = new APIManager();

const Api = {
  /**
   * [GETS] Coupon
   * --
   */
  getCoupons: async (params?: { [key: string]: string | number }) => {
    try {
      const res = await $http.get(APIConstant.GETS_COUPON, params);

      return res;
    } catch (e) {
      throw e;
    }
  },

  /**
   * [GETS] Coupon
   * --
   */
  getMapCoupons: async (params?: { [key: string]: string | number }) => {
    try {
      const res = await $http.get(APIConstant.GETS_MAP_COUPON, params);

      return res;
    } catch (e) {
      throw e;
    }
  },

  /**
   * [GETS] Coupon
   * --
   */
  getMapProductCoupons: async (params?: { [key: string]: string | number }) => {
    try {
      const res = await $http.get(APIConstant.GETS_MAP_PRODUCT_COUPON, params);

      return res;
    } catch (e) {
      throw e;
    }
  },

  /**
   * [GET] Coupon
   * --
   */
  getCoupon: async (
    idx: number,
    params?: { [key: string]: string | number }
  ) => {
    try {
      const url = parameterToPath(APIConstant.GET_COUPON, { coupon_id: idx });
      const res = await $http.get(url, params);

      return res;
    } catch (e) {
      throw e;
    }
  },

  /**
   * [POST] Coupon
   * --
   */
  createCoupon: async (body: Coupon) => {
    try {
      const res = await $http.post(APIConstant.CREATE_COUPON, body);

      return res;
    } catch (e) {
      throw e;
    }
  },

  /**
   * [POST] Coupon
   * --
   */
  mapCreateCoupon: async (body: MapCreateCouponRequest) => {
    try {
      const res = await $http.post(APIConstant.MAP_CREATE_COUPON, body);

      return res;
    } catch (e) {
      throw e;
    }
  },

  /**
   * [POST] Coupon
   * --
   */
  mapCreateProductCoupon: async (body: MapCreateProductCouponRequest) => {
    try {
      const res = await $http.post(APIConstant.MAP_CREATE_PRODUCT_COUPON, body);

      return res;
    } catch (e) {
      throw e;
    }
  },

  /**
   * [PUT] Coupon
   * --
   */
  updateCoupon: async (body: Coupon) => {
    try {
      const { coupon_id } = body;
      const url = parameterToPath(APIConstant.UPDATE_COUPON, {
        coupon_id: coupon_id,
      });

      const res = await $http.put(url, body);

      return res;
    } catch (e) {
      throw e;
    }
  },
  /**
   * [PUT] Coupon
   * --
   */
  updateBulkMapMenuPolicy: async (body: UpdateMapMenuPolicy[]) => {
    try {
      const url = APIConstant.BULK_UPDATE_MAP_MENU_POLICY;

      const res = await $http.put(url, body);

      return res;
    } catch (e) {
      throw e;
    }
  },
  /**
   * [DELETE] Coupon
   * --
   */
  deleteCoupon: async (idx: number) => {
    try {
      const url = parameterToPath(APIConstant.UPDATE_COUPON, {
        coupon_id: idx,
      });

      const res = await $http.delete(url);

      return res;
    } catch (e) {
      throw e;
    }
  },
};

export default Api;
